import * as Scrivito from "scrivito";

import moment from "moment";
import I18n from "^/config/i18n";
import getLanguage from "../../utils/getLanguage";
import courseDetailsMapping from "../../utils/courseDetailsMapping";

import { Detail } from "./Detail";

export const DefaultLayout = Scrivito.connect(({ obj, course, widget }) => {
  const language = getLanguage(Scrivito.currentPage());
  const mapping = courseDetailsMapping();
  const getDeadlineDates = (lang, dates = []) =>
    getTransletedDates(lang, dates).join(" & ");

  const translateMultipleElements = (data = []) =>
    data.map((el) => mapping[el]);

  const mergeAttributes = (customData, data = []) => {
    let output = [...data];
    if (customData && Array.isArray(customData)) {
      output = [...output, ...customData];
    } else if (customData) {
      output.push(customData);
    }
    return output;
  };

  const courseTypeDeadline = () =>
    course?.get("admissionType") === "enroll"
      ? I18n.t("CourseOver_deadline_enroll")
      : I18n.t("CourseOver_deadline_apply");

  const getTransletedDates = (lang, data = []) => {
    moment.locale(lang);

    return data.map((date) => {
      const d = moment(new Date(`${date} 2020`)).format("LL");

      if (lang === "de") {
        return d.slice(0, -5);
      }
      return d.slice(0, -6);
    });
  };

  const AttributeContent = Scrivito.connect(({ attribute }) => {
    switch (attribute) {
      case "credits":
        return `${obj.get("credits")} ${obj.get("creditsStandard")}`;
      case "deadline":
        return getDeadlineDates(
          language,
          mergeAttributes(obj.get("customDeadline"), obj.get("deadline"))
        );
      case "start":
        return mergeAttributes(
          obj.get("customStart"),
          translateMultipleElements(obj.get("start"))
        ).join(" & ");
      case "location":
        return mergeAttributes(
          obj.get("customLocation"),
          translateMultipleElements(obj.get("location"))
        ).join(" / ");
      case "lang":
        return translateMultipleElements(obj.get(attribute)).join(" / ");
      case "degree":
        return mapping[obj.get(attribute)];
      case "form":
        return translateMultipleElements(obj.get(attribute)).join(" / ");
      case "orientation":
        return translateMultipleElements(obj.get(attribute)).join(" / ");
      case "courseWorkload":
        return obj.get("courseWorkload");
      case "tuitionFee":
        return `${obj.get("tuitionFee")} €`;
      default:
        return obj.get(attribute);
    }
  });

  const AttributeTitle = Scrivito.connect(({ attribute }) => {
    let title;

    switch (attribute) {
      case "credits":
        title = `${obj.get("semester")} ${I18n.t("CourseOver_semester")}`;
        break;
      case "deadline":
        title = courseTypeDeadline();
        break;
      case "start":
        title = I18n.t("CourseOver_start");
        break;
      case "location":
        title = I18n.t("CourseOver_location");
        break;
      case "lang":
        title = I18n.t("CourseOver_language");
        break;
      case "degree":
        title = I18n.t("CourseOver_degree");
        break;
      case "form":
        title = I18n.t("CourseOver_form");
        break;
      case "orientation":
        title = I18n.t("CourseOver_orientation");
        break;
      case "courseWorkload":
        title = I18n.t("CourseOver_workload");
        break;
      case "tuitionFee":
        title = I18n.t("CourseOver_tuitionFee");
        break;
      default:
        title = attribute;
    }

    return <strong>{title}:</strong>;
  });

  const attributeIcons = {
    deadline: "calendar",
    credits: "book",
    start: "clock",
    location: "city",
    lang: "comments",
    degree: "user-graduate",
    form: "graduation-cap",
    orientation: "compass",
    courseWorkload: "tasks",
    tuitionFee: "wallet",
  };

  function capitalize(str) {
    if (!str) return ""; // Handle empty strings
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const value1 = widget.get("value1") || "deadline";
  const value2 = widget.get("value2") || "credits";
  const value3 = widget.get("value3") || "start";
  const value4 = widget.get("value4") || "location";
  const value5 = widget.get("value5") || "lang";
  const value6 = widget.get("value6") || "degree";
  const value7 = widget.get("value7") || "form";
  const value8 = widget.get("value8") || "empty";
  const value9 = widget.get("value9") || "empty";
  const value10 = widget.get("value10") || "empty";

  const Attribute = Scrivito.connect(
    ({ value, color, size, icon, position = "top" }) => {
      if (value === "empty") {
        return null;
      }

      const cssClasses =
        position === "left"
          ? "d-flex flex-column ms-md-1 align-md-self-center"
          : "";

      return (
        <Detail
          color={color}
          size={size}
          icon={icon}
          position={position}
          frontContent={
            <div className={cssClasses}>
              <AttributeTitle attribute={value} />
              <div>
                <AttributeContent attribute={value} />
              </div>
            </div>
          }
          backContent={
            <div className={cssClasses}>
              <AttributeTitle attribute={value} />
              <div>{obj.get(`additional${capitalize(value)}Info`)}</div>
            </div>
          }
        />
      );
    }
  );

  return (
    <div className="course-details row">
      <div className="col-12 col-lg-5">
        <div className="row">
          <div className="col-12 col-md-7">
            <Attribute
              value={value1}
              color="black"
              size="tall"
              icon={attributeIcons[value1]}
            />
          </div>

          <div className="col-12 col-md-5">
            <Attribute
              value={value2}
              color="blue"
              size="tall"
              icon={attributeIcons[value2]}
            />
          </div>
        </div>

        <div className="col-12">
          <Attribute
            value={value3}
            color="orange"
            size="tall"
            icon={attributeIcons[value3]}
          />
        </div>
      </div>

      <div className="col-12 col-lg-7">
        <div className="row">
          <div className="col-12 col-md-6">
            <Attribute
              value={value4}
              color="teal"
              size="tall"
              icon={attributeIcons[value4]}
            />
          </div>

          <div className="col-12 col-md-6">
            <Attribute
              value={value5}
              color="orange"
              size={value6 === "empty" ? "tall" : "default"}
              position={value6 === "empty" ? "top" : "left"}
              icon={attributeIcons[value5]}
            />

            <Attribute
              value={value6}
              color="blue"
              size={value5 === "empty" ? "tall" : "default"}
              position={value5 === "empty" ? "top" : "left"}
              icon={attributeIcons[value6]}
            />
          </div>

          <div className="col-12">
            <Attribute
              value={value7}
              color="yellow"
              size="tall"
              icon={attributeIcons[value7]}
            />
          </div>
        </div>
      </div>
      <div className="col-12">
        <div className="row">
          <div className="col-12 col-md-4">
            <Attribute
              value={value8}
              color="black"
              size="tall"
              icon={attributeIcons[value8]}
            />
          </div>
          <div className="col-12 col-md-4">
            <Attribute
              value={value9}
              color="teal"
              size="tall"
              icon={attributeIcons[value9]}
            />
          </div>
          <div className="col-12 col-md-4">
            <Attribute
              value={value10}
              color="blue"
              size="tall"
              icon={attributeIcons[value10]}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
